// The app's own Lambda: kitchen questions and the after-dinner debrief.
// Authenticated with the Meal Hat Firebase ID token; the Lambda verifies it
// against project `meal-hat` and nothing else.
import { authToken } from '../store/db.js';
import { askStepLine } from './recipes.js';

const API_URL = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');

async function call (route, body) {
  if (!API_URL) throw new Error('The kitchen helper is not configured (VUE_APP_API_URL).');
  const token = await authToken();
  if (!token) throw new Error('Sign in first.');
  const response = await fetch(`${API_URL}${route}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body)
  });
  const text = await response.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }
  if (!response.ok) throw new Error(data?.error || `The kitchen helper responded ${response.status}`);
  return data;
}

const ingredientLine = (item) => (item.qty ? `${item.name}: ${item.qty}` : item.name);

/** A question about a lesson. `phase` is 'cooking' (mid-cook, short and
 *  urgent), 'prep' (cook mode's prep screens; `stepIndex` is then the prep
 *  step) or 'before' (from the lesson page, nothing on the heat yet).
 *  `voice` asks for an answer made to be read aloud (AskBox's Talk out
 *  loud). Returns { answer }. */
export const askQuestion = ({ lesson, stepIndex, question, history = [], phase = 'cooking', voice = false }) =>
  call('/ask', {
    lessonId: lesson.id,
    lessonTitle: lesson.title,
    dish: lesson.dish,
    side: lesson.side,
    steps: lesson.steps.map(askStepLine),
    mise: lesson.mise.map((item) => `${item.text}${item.needs?.length ? ` Needs: ${item.needs.join(', ')}.` : ''} How: ${item.how}`),
    ingredients: [...(lesson.order || []), ...(lesson.grocery || [])].map(ingredientLine),
    equipment: lesson.equipment || [],
    phase,
    stepIndex,
    question,
    history,
    voice
  });

/** One message to the teacher (TeacherChat.vue). Returns { answer, actions }.
 *  `probe` is the latest thermometer reading, if cook mode has one. */
export const askTeacher = ({ question, where, recipe, history = [], voice = false, notes = '', debriefs = [], plan = '', pace = '', preferences = {}, probe = null }) =>
  call('/chat', { question, where, recipe, history, voice, notes, debriefs, plan, pace, preferences, probe });

/** The ThermoMaven probe, read now (Lambda /probe). Returns { readings, readAt }. */
export const readProbe = () => call('/probe', {});

/** The after-dinner talk. Returns the structured summary. */
export const summarizeDebrief = ({ lesson, transcript, previous = [], notes = '' }) =>
  call('/debrief', {
    notes,
    lessonId: lesson.id,
    lessonTitle: lesson.title,
    dish: lesson.dish,
    side: lesson.side,
    prompt: lesson.debriefPrompt,
    transcript,
    previous
  });
